import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { Plus, Trash2, Settings, AlertTriangle, Copy, Check, ChevronDown, Info } from 'lucide-react';

function Tooltip({ text }: { text: string }) {
  const [visible, setVisible] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 4 }} onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      <Info size={13} className="pk-i" style={{ cursor: 'help' }} />
      {visible && (
        <span
          className="pk-small"
          style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 8,
            width: 224, background: 'var(--arc-ink)', color: 'var(--arc-paper)', borderRadius: 'var(--arc-r-sm)',
            padding: '7px 9px', zIndex: 10001, lineHeight: 1.5, boxShadow: 'var(--arc-sh-2)', whiteSpace: 'normal',
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

interface ApiKey {
  id: string;
  name: string;
  key?: string;
  isActive: boolean;
  usageCount: number;
  creditsUsed: number;
  lastUsedAt?: string;
  createdAt: string;
  modelSelectionMode?: 'all' | 'custom_only' | 'global_only' | 'privacy_only';
  modelSelectionPriority?: 'rank' | 'price';
  modelListId?: string;
  activeTagSlugs?: string[];
  taskDeterminationEnabled?: boolean;
  spendWindows?: {
    frequency: 'daily' | 'weekly' | 'monthly';
    spendLimit: number | null;
    currentSpend: number;
    periodExpiresAt: string;
  }[];
}

interface ModelList {
  id: string;
  name: string;
  description?: string;
  items?: { modelId: string }[];
}

interface Category {
  slug: string;
  name: string;
}

export default function ApiKeys() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [modelLists, setModelLists] = useState<ModelList[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [pendingConfigKeyId, setPendingConfigKeyId] = useState<string | null>(null);
  const [showKeySettingsModal, setShowKeySettingsModal] = useState(false);
  const [keyModelSelectionMode, setKeyModelSelectionMode] = useState<'all' | 'custom_only' | 'global_only' | 'privacy_only'>('all');
  const [keyModelSelectionPriority, setKeyModelSelectionPriority] = useState<'rank' | 'price'>('rank');
  const [keyModelListId, setKeyModelListId] = useState<string>('');
  const [keyActiveTagSlugs, setKeyActiveTagSlugs] = useState<string[]>([]);
  const [keyTaskDeterminationEnabled, setKeyTaskDeterminationEnabled] = useState<boolean>(true);
  const [keyDailySpendLimit, setKeyDailySpendLimit] = useState<string>('');
  const [keyWeeklySpendLimit, setKeyWeeklySpendLimit] = useState<string>('');
  const [keyMonthlySpendLimit, setKeyMonthlySpendLimit] = useState<string>('');
  const [accountDailyLimit, setAccountDailyLimit] = useState<string>('');
  const [accountWeeklyLimit, setAccountWeeklyLimit] = useState<string>('');
  const [accountMonthlyLimit, setAccountMonthlyLimit] = useState<string>('');
  const [accountTdOverage, setAccountTdOverage] = useState<boolean>(false);
  const [accountSpendWindows, setAccountSpendWindows] = useState<{ frequency: string; currentSpend: number; periodExpiresAt: string }[]>([]);
  const [tagModelCache, setTagModelCache] = useState<Record<string, string[]>>({});
  const [accountLimitsSaving, setAccountLimitsSaving] = useState(false);
  const [accountLimitsSaved, setAccountLimitsSaved] = useState(false);
  const [accountLimitsOpen, setAccountLimitsOpen] = useState(false);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [tagDropdownRect, setTagDropdownRect] = useState<DOMRect | null>(null);
  const tagDropdownRef = useRef<HTMLDivElement>(null);
  const tagTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    loadApiKeys();
    loadModelLists();
    loadCategories();
    loadAccountLimits();
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target as Node)) {
        setTagDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const uncached = keyActiveTagSlugs.filter(slug => !(slug in tagModelCache));
    if (uncached.length === 0) return;
    Promise.all(
      uncached.map(slug =>
        apiService.getTagModels(slug)
          .then(res => ({ slug, modelIds: res.modelIds }))
          .catch(() => ({ slug, modelIds: [] as string[] }))
      )
    ).then(results => {
      setTagModelCache(prev => {
        const next = { ...prev };
        results.forEach(r => { next[r.slug] = r.modelIds; });
        return next;
      });
    });
  }, [keyActiveTagSlugs]);

  const intersectionWarning = useMemo(() => {
    if (!keyModelListId || keyActiveTagSlugs.length === 0) return null;
    const list = modelLists.find(l => l.id === keyModelListId);
    if (!list?.items?.length) return null;
    const listModelIds = new Set(list.items.map((i: any) => i.modelId));
    const emptyTags: string[] = [];
    for (const slug of keyActiveTagSlugs) {
      if (!(slug in tagModelCache)) continue;
      const tagModels = tagModelCache[slug];
      if (tagModels.length === 0) continue;
      const hasOverlap = tagModels.some(id => listModelIds.has(id));
      if (!hasOverlap) {
        const cat = categories.find(c => c.slug === slug);
        emptyTags.push(cat?.name || slug);
      }
    }
    if (emptyTags.length === 0) return null;
    return `No models overlap between "${list.name}" and the ${emptyTags.map(t => `"${t}"`).join(', ')} tag${emptyTags.length > 1 ? 's' : ''}. These tag filters will be silently skipped at runtime.`;
  }, [keyModelListId, keyActiveTagSlugs, tagModelCache, modelLists, categories]);

  const loadModelLists = async () => {
    try {
      const res = await apiService.getModelLists();
      setModelLists((res as any).data || res);
    } catch {
      // non-fatal
    }
  };

  const loadCategories = async () => {
    try {
      const data = await apiService.listCategories();
      setCategories((data.categories || []).filter((c: Category) => c.slug !== 'privacy'));
    } catch {
      // non-fatal
    }
  };

  const loadAccountLimits = async () => {
    try {
      const data = await apiService.getAccountLimits();
      setAccountDailyLimit(data.dailySpendLimit != null ? String(data.dailySpendLimit) : '');
      setAccountWeeklyLimit(data.weeklySpendLimit != null ? String(data.weeklySpendLimit) : '');
      setAccountMonthlyLimit(data.monthlySpendLimit != null ? String(data.monthlySpendLimit) : '');
      setAccountSpendWindows(data.spendWindows || []);
      setAccountTdOverage(data.tdOverageEnabled ?? false);
    } catch {
      // non-fatal — server might not have the endpoint yet
    }
  };

  const handleSaveAccountLimits = async () => {
    setAccountLimitsSaving(true);
    try {
      await apiService.updateAccountLimits({
        dailySpendLimit:   accountDailyLimit   !== '' ? parseFloat(accountDailyLimit)   : null,
        weeklySpendLimit:  accountWeeklyLimit  !== '' ? parseFloat(accountWeeklyLimit)  : null,
        monthlySpendLimit: accountMonthlyLimit !== '' ? parseFloat(accountMonthlyLimit) : null,
        tdOverageEnabled:  accountTdOverage,
      });
      setAccountLimitsSaved(true);
      setTimeout(() => setAccountLimitsSaved(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save account limits');
    } finally {
      setAccountLimitsSaving(false);
    }
  };

  const handleEditKeySettings = (key: ApiKey) => {
    setEditingKey(key);
    setKeyModelSelectionMode(key.modelSelectionMode || 'all');
    setKeyModelSelectionPriority(key.modelSelectionPriority || 'rank');
    setKeyModelListId(key.modelListId || '');
    setKeyActiveTagSlugs(key.activeTagSlugs || []);
    setKeyTaskDeterminationEnabled(key.taskDeterminationEnabled ?? true);
    const windows = key.spendWindows || [];
    const w = (freq: string) => windows.find(x => x.frequency === freq);
    setKeyDailySpendLimit(w('daily')?.spendLimit != null ? String(w('daily')!.spendLimit) : '');
    setKeyWeeklySpendLimit(w('weekly')?.spendLimit != null ? String(w('weekly')!.spendLimit) : '');
    setKeyMonthlySpendLimit(w('monthly')?.spendLimit != null ? String(w('monthly')!.spendLimit) : '');
    setShowKeySettingsModal(true);
  };

  const handleSaveKeySettings = async () => {
    if (!editingKey) return;

    try {
      await apiService.updateApiKey(editingKey.id, {
        modelSelectionMode: keyModelSelectionMode,
        modelSelectionPriority: keyModelSelectionPriority,
        modelListId: keyModelListId || null,
        activeTagSlugs: keyActiveTagSlugs,
        taskDeterminationEnabled: keyTaskDeterminationEnabled,
      });
      // Save spend limits per-frequency (only if changed)
      await Promise.all(([
        ['daily',   keyDailySpendLimit],
        ['weekly',  keyWeeklySpendLimit],
        ['monthly', keyMonthlySpendLimit],
      ] as const).map(([freq, val]) =>
        apiService.setKeySpendWindow(editingKey.id, freq, val !== '' ? parseFloat(val) : null)
      ));
      setShowKeySettingsModal(false);
      setEditingKey(null);
      await loadApiKeys();
    } catch (err: any) {
      setError(err.message || 'Failed to save API key settings');
    }
  };

  const loadApiKeys = async () => {
    try {
      const keys = await apiService.getApiKeys();
      setApiKeys(keys);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;

    try {
      const result = await apiService.createApiKey(newKeyName.trim());
      setCreatedKey(result.key);
      setPendingConfigKeyId(result.id);
      setNewKeyName('');
      setShowCreateModal(false);
      await loadApiKeys();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.revokeApiKey(keyId);
      await loadApiKeys();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const maskKey = (keyId: string) => {
    // Always show the key ID with masking - full keys are never returned after creation
    return `fxk_${keyId.substring(0, 8)}••••••••••••••••••••••••••••••••••••••••••••••••••••`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const copyCreatedKey = () => {
    if (!createdKey) return;
    navigator.clipboard.writeText(createdKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 3000);
  };

  const dismissCreatedKey = () => {
    setCreatedKey(null);
    setKeyCopied(false);
    if (pendingConfigKeyId) {
      const key = apiKeys.find(k => k.id === pendingConfigKeyId);
      setPendingConfigKeyId(null);
      if (key) handleEditKeySettings(key);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 256 }}>
        <div className="pk-skel" style={{ width: 32, height: 32, borderRadius: '50%' }} />
      </div>
    );
  }

  return (
    <>
    <div>
      <div className="pk-page-head">
        <div className="col">
          <h1 className="pk-h1">API Keys</h1>
          <p className="pk-sub">Manage your API keys for accessing AskArc API</p>
        </div>
        <div className="actions">
          <button type="button" onClick={() => setShowCreateModal(true)} className="pk-btn pk-btn-grad">
            <Plus size={16} />
            Create API Key
          </button>
        </div>
      </div>

      {error && (
        <div className="pk-note pk-note-danger" style={{ marginBottom: 20 }}>
          {error}
        </div>
      )}

      {/* Account-level spend limits */}
      <div className="pk-card pk-card-flush" style={{ marginBottom: 20 }}>
        <button
          type="button"
          onClick={() => setAccountLimitsOpen(o => !o)}
          className="pk-card-head"
          style={{ width: '100%', textAlign: 'left', border: 0, background: 'none', cursor: 'pointer', borderBottom: accountLimitsOpen ? undefined : 0 }}
        >
          <div className="col">
            <h2 className="pk-h4">Account Spend Limits</h2>
            <p className="pk-small">Global caps applied across all API keys</p>
          </div>
          <div className="actions">
            <ChevronDown size={18} className="pk-i" style={{ transition: 'transform var(--arc-t) var(--arc-ease)', transform: accountLimitsOpen ? 'rotate(180deg)' : undefined }} />
          </div>
        </button>

        {accountLimitsOpen && (
          <div className="pk-card-body" style={{ paddingTop: 4 }}>
            <div className="pk-grid pk-grid-3">
              {(['daily', 'weekly', 'monthly'] as const).map(period => {
                const val = period === 'daily' ? accountDailyLimit : period === 'weekly' ? accountWeeklyLimit : accountMonthlyLimit;
                const setter = period === 'daily' ? setAccountDailyLimit : period === 'weekly' ? setAccountWeeklyLimit : setAccountMonthlyLimit;
                const limitNum = parseFloat(val);
                const window = accountSpendWindows.find(w => w.frequency === period);
                const currentSpend = window?.currentSpend ?? 0;
                const limitExceeded = !isNaN(limitNum) && currentSpend >= limitNum;
                return (
                  <div key={period} className="pk-field">
                    <label className="pk-field-label" style={{ textTransform: 'capitalize' }}>{period} Limit ($)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Unlimited"
                      value={val}
                      onChange={e => setter(e.target.value)}
                      className="pk-input"
                    />
                    {window && (
                      <p className="pk-help">
                        <span style={{ color: limitExceeded ? 'var(--arc-danger)' : 'var(--arc-ink-2)', fontWeight: limitExceeded ? 700 : 400 }}>${currentSpend.toFixed(4)}</span>
                        {!isNaN(limitNum) ? ` / $${limitNum.toFixed(2)} this ${period}` : ' spent this ' + period}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <span className="pk-switch">
                  <input
                    type="checkbox"
                    checked={accountTdOverage}
                    onChange={e => setAccountTdOverage(e.target.checked)}
                  />
                  <i />
                </span>
                <span className="pk-small" style={{ color: 'var(--arc-ink-2)' }}>Allow Task Determination overage billing</span>
              </label>
              <Tooltip text="When your free TD quota is exhausted: OFF = requests are blocked, ON = TD calls continue at the overage rate." />
            </div>

            <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={handleSaveAccountLimits} disabled={accountLimitsSaving} className="pk-btn pk-btn-ink pk-btn-sm">
                {accountLimitsSaving ? 'Saving…' : 'Save Account Limits'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Created Key Modal */}
      {createdKey && (
        <div className="pk-scrim" style={{ position: 'fixed', zIndex: 9999 }} onClick={() => setCreatedKey(null)}>
          <div className="pk-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="pk-note pk-note-warning" style={{ borderRadius: 0, borderWidth: '0 0 1px', margin: 0 }}>
              <AlertTriangle size={18} />
              <div>
                <p style={{ fontWeight: 700, color: 'var(--arc-ink)', margin: 0 }}>Save your API key now</p>
                <p className="pk-small" style={{ marginTop: 3 }}>
                  This is the <strong>only time</strong> your full key will be shown. It cannot be recovered after you close this dialog.
                </p>
              </div>
            </div>

            <div className="pk-modal-body">
              <div>
                <p className="pk-small" style={{ marginBottom: 10 }}>Your new API key:</p>
                <div className="pk-secret" style={{ height: 'auto', minHeight: 40, whiteSpace: 'normal', wordBreak: 'break-all' }}>
                  <span className="val" style={{ whiteSpace: 'normal', overflow: 'visible' }}>{createdKey}</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={copyCreatedKey} className={`pk-btn ${keyCopied ? '' : 'pk-btn-ink'} pk-btn-block`} style={keyCopied ? { background: 'var(--arc-success)', color: '#fff', border: 0 } : undefined}>
                  {keyCopied ? (<><Check size={16} /> Copied!</>) : (<><Copy size={16} /> Copy API Key</>)}
                </button>
                <button type="button" onClick={dismissCreatedKey} className="pk-btn pk-btn-outline pk-btn-block">
                  I've saved it — configure key →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="pk-scrim" style={{ position: 'fixed', zIndex: 9999 }} onClick={() => setShowCreateModal(false)}>
          <div className="pk-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="pk-modal-head">
              <div className="col">
                <h3 className="pk-h3">Create New API Key</h3>
              </div>
            </div>
            <div className="pk-modal-body">
              <div className="pk-field">
                <input
                  type="text"
                  placeholder="Enter key name"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  className="pk-input"
                  autoFocus
                />
              </div>
            </div>
            <div className="pk-modal-foot">
              <div className="spacer" />
              <button type="button" onClick={() => setShowCreateModal(false)} className="pk-btn pk-btn-outline">
                Cancel
              </button>
              <button type="button" onClick={handleCreateKey} disabled={!newKeyName.trim()} className="pk-btn pk-btn-ink">
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Key Settings Modal */}
      {showKeySettingsModal && editingKey && (
        <div className="pk-scrim" style={{ position: 'fixed', zIndex: 9999, padding: 16 }} onClick={() => { setShowKeySettingsModal(false); setEditingKey(null); setTagDropdownOpen(false); setTagSearch(''); }}>
          <div className="pk-modal" style={{ maxWidth: 720, maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="pk-modal-head">
              <div className="col">
                <h3 className="pk-h3">API Key Settings: {editingKey.name}</h3>
                <p className="pk-sub">Configure how this API key interacts with models</p>
              </div>
            </div>

            {/* Body — 2 columns */}
            <div className="pk-modal-body">
              <div className="pk-grid pk-grid-2">

                {/* Left column — selection behaviour */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div className="pk-field">
                    <label className="pk-field-label">
                      Model Selection Mode
                      <Tooltip text="Controls which models this key can access. 'All' uses global + custom. 'Privacy-Compliant' fails if no privacy models are configured." />
                    </label>
                    <select
                      value={keyModelSelectionMode}
                      onChange={(e) => setKeyModelSelectionMode(e.target.value as 'all' | 'custom_only' | 'global_only' | 'privacy_only')}
                      className="pk-select"
                    >
                      <option value="all">All Models (Global + Custom)</option>
                      <option value="custom_only">My Custom Models Only</option>
                      <option value="global_only">Global Models Only</option>
                      <option value="privacy_only">Privacy-Compliant Only</option>
                    </select>
                  </div>

                  <div className="pk-field">
                    <label className="pk-field-label">
                      Model Selection Priority
                      <Tooltip text="Rank First picks the highest-quality model. Price First picks the most cost-effective model in the eligible pool." />
                    </label>
                    <select
                      value={keyModelSelectionPriority}
                      onChange={(e) => setKeyModelSelectionPriority(e.target.value as 'rank' | 'price')}
                      className="pk-select"
                    >
                      <option value="rank">Rank First (Quality)</option>
                      <option value="price">Price First (Cost)</option>
                    </select>
                  </div>

                  <div className="pk-field">
                    <label className="pk-field-label">
                      Task Determination
                      <Tooltip text="When forced, the system always picks the best model and ignores any model passed in the request. When off, the caller controls model selection — TD still runs if no model is specified." />
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <span className="pk-switch">
                        <input
                          type="checkbox"
                          checked={keyTaskDeterminationEnabled}
                          onChange={(e) => setKeyTaskDeterminationEnabled(e.target.checked)}
                        />
                        <i />
                      </span>
                      <span className="pk-small" style={{ color: 'var(--arc-ink-2)' }}>Force Task Determination</span>
                    </label>
                  </div>
                </div>

                {/* Right column — restrictions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div className="pk-field">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <label className="pk-field-label">
                        Model List <span className="pk-small" style={{ fontWeight: 400 }}>(optional)</span>
                        <Tooltip text="Restricts this key to only models in a curated list. If the list is empty the request will fail." />
                      </label>
                      <Link to="/models" className="pk-btn-text" style={{ fontSize: 12.5 }}>Manage →</Link>
                    </div>
                    <select
                      value={keyModelListId}
                      onChange={(e) => setKeyModelListId(e.target.value)}
                      className="pk-select"
                    >
                      <option value="">No list — all available models</option>
                      {modelLists.map(list => (
                        <option key={list.id} value={list.id}>
                          {list.name}{list.items?.length !== undefined ? ` (${list.items.length} model${list.items.length !== 1 ? 's' : ''})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {categories.length > 0 && (
                    <div className="pk-field">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <label className="pk-field-label">
                          Tag-Based Filtering <span className="pk-small" style={{ fontWeight: 400 }}>(optional)</span>
                          <Tooltip text="When a tag fires on a request to this key, only models with that tag enabled will be used." />
                        </label>
                        <Link to="/category-rules" className="pk-btn-text" style={{ fontSize: 12.5 }}>Manage →</Link>
                      </div>
                      <div style={{ position: 'relative' }} ref={tagDropdownRef}>
                        <button
                          ref={tagTriggerRef}
                          type="button"
                          onClick={() => {
                            if (tagTriggerRef.current) setTagDropdownRect(tagTriggerRef.current.getBoundingClientRect());
                            setTagDropdownOpen(o => !o);
                          }}
                          className="pk-select"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left', cursor: 'pointer' }}
                        >
                          <span style={{ color: keyActiveTagSlugs.length === 0 ? 'var(--arc-ink-4)' : 'var(--arc-ink)' }}>
                            {keyActiveTagSlugs.length === 0
                              ? 'No tags selected'
                              : `${keyActiveTagSlugs.length} tag${keyActiveTagSlugs.length !== 1 ? 's' : ''} selected`}
                          </span>
                          <ChevronDown size={14} className="pk-i" style={{ transition: 'transform var(--arc-t) var(--arc-ease)', transform: tagDropdownOpen ? 'rotate(180deg)' : undefined }} />
                        </button>

                        {tagDropdownOpen && tagDropdownRect && (
                          <div
                            ref={tagDropdownRef}
                            className="pk-card pk-card-flush"
                            style={{
                              position: 'fixed', zIndex: 10000, boxShadow: 'var(--arc-sh-2)',
                              top: tagDropdownRect.bottom + 4,
                              left: tagDropdownRect.left,
                              width: tagDropdownRect.width,
                            }}
                          >
                            <div style={{ padding: 8, borderBottom: '1px solid var(--arc-line-2)' }}>
                              <input
                                type="text"
                                value={tagSearch}
                                onChange={e => setTagSearch(e.target.value)}
                                placeholder="Search tags…"
                                className="pk-input pk-small"
                                style={{ minHeight: 32 }}
                                autoFocus
                                onClick={e => e.stopPropagation()}
                              />
                            </div>
                            <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                              {categories.filter(cat => cat.name.toLowerCase().includes(tagSearch.toLowerCase())).map(cat => {
                                const checked = keyActiveTagSlugs.includes(cat.slug);
                                return (
                                  <label key={cat.slug} className="pk-nav-item" style={{ padding: '8px 12px', minHeight: 'auto', cursor: 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setKeyActiveTagSlugs(prev => [...prev, cat.slug]);
                                        } else {
                                          setKeyActiveTagSlugs(prev => prev.filter(s => s !== cat.slug));
                                        }
                                      }}
                                    />
                                    <span className="pk-small" style={{ color: 'var(--arc-ink-2)' }}>{cat.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {intersectionWarning && (
                    <div className="pk-note pk-note-warning" style={{ padding: '9px 12px' }}>
                      <AlertTriangle size={14} />
                      <p className="pk-small">{intersectionWarning}</p>
                    </div>
                  )}
                </div>

              </div>

              {/* Spend Limits — full-width section below the two columns */}
              <div style={{ paddingTop: 18, borderTop: '1px solid var(--arc-line-2)' }}>
                <h4 className="pk-h4" style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  Spend Limits
                  <Tooltip text="Leave blank for unlimited. All three can be set independently. Resets: daily at midnight UTC, weekly on Monday, monthly on the 1st." />
                </h4>
                <div className="pk-grid pk-grid-3">
                  {([
                    { label: 'Daily ($)', freq: 'daily'   as const, val: keyDailySpendLimit,   setter: setKeyDailySpendLimit },
                    { label: 'Weekly ($)', freq: 'weekly'  as const, val: keyWeeklySpendLimit,  setter: setKeyWeeklySpendLimit },
                    { label: 'Monthly ($)', freq: 'monthly' as const, val: keyMonthlySpendLimit, setter: setKeyMonthlySpendLimit },
                  ]).map(({ label, freq, val, setter }) => {
                    const win = editingKey?.spendWindows?.find(w => w.frequency === freq);
                    return (
                      <div key={freq} className="pk-field">
                        <label className="pk-field-label" style={{ fontSize: 12.5 }}>{label}</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Unlimited"
                          value={val}
                          onChange={e => setter(e.target.value)}
                          className="pk-input"
                        />
                        {win && (
                          <p className="pk-help">
                            Spent: <span style={{ fontWeight: 700, color: 'var(--arc-ink-2)' }}>${win.currentSpend.toFixed(4)}</span>
                            {val !== '' && ` / $${parseFloat(val).toFixed(2)}`}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="pk-modal-foot">
              <div className="spacer" />
              <button
                type="button"
                onClick={() => { setShowKeySettingsModal(false); setEditingKey(null); setTagDropdownOpen(false); setTagSearch(''); }}
                className="pk-btn pk-btn-outline"
              >
                Cancel
              </button>
              <button type="button" onClick={handleSaveKeySettings} className="pk-btn pk-btn-ink">
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys List */}
      {apiKeys.length === 0 ? (
        <div className="pk-empty">
          <div className="pk-i-plate"><Settings size={18} /></div>
          <p className="pk-sub">No API keys yet. Create your first one to get started.</p>
        </div>
      ) : (
        <div className="pk-table-wrap pk-table-grad">
          {apiKeys.map((key, i) => (
            <div
              key={key.id}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
                padding: '16px 18px', borderBottom: i < apiKeys.length - 1 ? '1px solid var(--arc-line-3)' : 0,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 className="pk-h4">{key.name}</h3>

                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 16 }} className="pk-small">
                  <span>Usage: {key.usageCount} requests</span>
                  <span>Cost: ${key.creditsUsed.toFixed(4)}</span>
                  <span>Last used: {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : 'Never'}</span>
                  <span>Created: {new Date(key.createdAt).toLocaleDateString()}</span>
                </div>
                {(key.spendWindows || []).filter(w => w.spendLimit != null).length > 0 && (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 12 }} className="pk-small">
                    {(key.spendWindows || []).filter(w => w.spendLimit != null).map(w => (
                      <span key={w.frequency}>
                        <span style={{ textTransform: 'capitalize' }}>{w.frequency}</span>:{' '}
                        <span style={{ fontWeight: 700, color: w.currentSpend >= w.spendLimit! ? 'var(--arc-danger)' : 'var(--arc-ink-2)' }}>
                          ${w.currentSpend.toFixed(4)}
                        </span>
                        {' / '}${w.spendLimit!.toFixed(2)}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 'none' }}>
                <span className={`pk-badge ${key.isActive ? 'pk-badge-success' : 'pk-badge-danger'}`}>
                  {key.isActive ? 'Active' : 'Revoked'}
                </span>
                {key.isActive && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleEditKeySettings(key)}
                      className="pk-btn pk-btn-quiet pk-btn-icon"
                      title="Configure settings for this API key"
                    >
                      <Settings size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRevokeKey(key.id)}
                      className="pk-btn pk-btn-quiet pk-btn-icon"
                      style={{ color: 'var(--arc-danger)' }}
                      title="Revoke this API key"
                    >
                      <Trash2 size={18} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

      {accountLimitsSaved && (
        <div className="pk-toast pk-toast-success" style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50, boxShadow: 'var(--arc-sh-2)' }}>
          Account limits saved successfully
        </div>
      )}
    </>
  );
}
